import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setAirportData } from '../Redux/Actions/Airport';

/**
 * Helper: read display mode from all available sources (synchronous).
 * Priority: NativeFIDS interface > window vars > localStorage
 */
function getDisplayMode() {
  try {
    if (typeof window.NativeFIDS !== 'undefined') {
      return window.NativeFIDS.getDisplayMode();
    }
  } catch (e) { /* not on Android */ }
  return window.ANDROID_DISPLAY_MODE || localStorage.getItem('fids_display_mode') || null;
}

function getStartRoute() {
  try {
    if (typeof window.NativeFIDS !== 'undefined') {
      return window.NativeFIDS.getStartRoute();
    }
  } catch (e) { /* not on Android */ }
  return window.ANDROID_START_ROUTE || localStorage.getItem('fids_start_route') || null;
}

const ModeRedirector = ({ children }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const airportState = useSelector((state) => state.Airport.Airport);
  const [retryCount, setRetryCount] = useState(0);

  // On Capacitor, block rendering until display mode is resolved to prevent
  // the default route (arrivals) flashing before redirect to departures.
  // The pre-React script in index.html should have already set the hash via
  // NativeFIDS, so this is primarily a safety net.
  const [modeResolved, setModeResolved] = useState(() => {
    if (typeof window === 'undefined' || !window.Capacitor) return true;

    const mode = getDisplayMode();
    if (!mode) return false; // Will retry in useEffect

    // We have the mode. The index.html script should have already set the
    // correct hash, so just check if the current hash matches.
    const route = getStartRoute() || (mode === 'departures' ? '/departure' : '/arrival');
    const currentHash = window.location.hash.replace('#', '');
    if (currentHash.includes(route.replace('/', ''))) {
      return true; // Hash already correct
    }

    // Hash not yet correct — the index.html script may not have run.
    // Force it now synchronously before React Router reads the hash.
    try {
      window.location.hash = '#' + route;
    } catch (e) { /* ignore */ }
    return true;
  });

  useEffect(() => {
    const currentHash = window.location.hash.replace('#', '');

    // Don't redirect if we're on admin routes
    if (currentHash.includes('admin') || currentHash.includes('login') || currentHash.includes('register')) {
      console.log('ModeRedirector: On admin/auth route, skipping redirect');
      setModeResolved(true);
      return;
    }

    // Check if we're in Capacitor (Android)
    const isCapacitor = window.Capacitor !== undefined;

    // For Capacitor apps, check MainActivity's configured route
    if (isCapacitor) {
      const androidDisplayMode = getDisplayMode();
      const androidStartRoute = getStartRoute();

      console.log(`ModeRedirector: displayMode=${androidDisplayMode}, startRoute=${androidStartRoute}, hash=${currentHash}, retry=${retryCount}`);

      // If mode still not available, wait and retry (up to 20 times = 1s total)
      if (!androidDisplayMode && !androidStartRoute && retryCount < 20) {
        console.log(`ModeRedirector: Variables not set yet, retrying in 50ms (attempt ${retryCount + 1}/20)...`);
        const timer = setTimeout(() => {
          setRetryCount(prev => prev + 1);
        }, 50);
        return () => clearTimeout(timer);
      }

      // Mode is now resolved (either found or retries exhausted)
      setModeResolved(true);

      // Persist to localStorage and window for other components
      if (androidDisplayMode) {
        window.ANDROID_DISPLAY_MODE = androidDisplayMode;
        localStorage.setItem('fids_display_mode', androidDisplayMode);
      }
      if (androidStartRoute) {
        window.ANDROID_START_ROUTE = androidStartRoute;
        localStorage.setItem('fids_start_route', androidStartRoute);
      }

      // If we need to navigate to the start route, do it
      if (androidStartRoute && !currentHash.includes(androidStartRoute.replace('/', ''))) {
        console.log(`ModeRedirector: Navigating to configured route: ${androidStartRoute}`);
        navigate(androidStartRoute, { replace: true });

        const wing = androidStartRoute.includes('departure') ? 'departure' : 'arrival';
        if (!airportState?.set || airportState?.airport === '' || airportState?.wing === '') {
          console.log(`ModeRedirector: Initializing airport state for ${wing} page`);
          dispatch(setAirportData({
            airport: 'LOS',
            terminal: '1',
            wing: wing,
            set: true
          }));
        }
        return;
      }

      // Already on correct route, just initialize airport state
      if (!airportState?.set || airportState?.airport === '' || airportState?.wing === '') {
        const wing = currentHash.includes('departure') ? 'departure' : 'arrival';
        console.log(`ModeRedirector: Already on correct route, initializing airport state for ${wing} page`);
        dispatch(setAirportData({
          airport: 'LOS',
          terminal: '1',
          wing: wing,
          set: true
        }));
      }
      return;
    }

    // Not Capacitor - always resolved
    setModeResolved(true);

    // Check if we're in Electron
    const isElectron = window.electron && window.electron.getMode;
    const electronMode = isElectron ? window.electron.getMode() : null;

    console.log(`ModeRedirector: Current hash: ${currentHash}`);
    console.log(`ModeRedirector: Is Electron: ${!!isElectron}, mode: ${electronMode || 'none'}`);

    // Determine which mode to use
    const displayMode = electronMode;

    // Only redirect for Electron apps, NOT for web browsers
    // Web users should be able to navigate freely between arrival/departure pages
    if (displayMode && isElectron) {
      console.log(`Display mode detected: ${displayMode}`);

      // Determine wing based on display mode
      const wing = displayMode === 'arrivals' ? 'arrival' : 'departure';

      // Initialize airport state if not set (for direct navigation/Capacitor/Electron apps)
      if (!airportState?.set || airportState?.airport === '' || airportState?.wing === '') {
        console.log(`Initializing airport state for ${displayMode} mode`);
        dispatch(setAirportData({
          airport: 'LOS',
          terminal: '1',
          wing: wing,
          set: true
        }));
      }

      // Redirect based on mode
      if (displayMode === 'arrivals' && !currentHash.includes('arrival')) {
        console.log('Redirecting to /arrival');
        navigate('/arrival', { replace: true });
      } else if (displayMode === 'departures' && !currentHash.includes('departure')) {
        console.log('Redirecting to /departure');
        navigate('/departure', { replace: true });
      } else {
        console.log('Already on correct route, no redirect needed');
      }
    } else {
      console.log('Web browser mode - no redirect, checking airport state');

      // For web browsers, initialize airport state based on current route if not set
      // Also handle root '/' route which renders arrivals by default
      if (!airportState?.set || airportState?.airport === '' || airportState?.wing === '') {
        const wing = currentHash.includes('departure') ? 'departure' : 'arrival';
        console.log(`Initializing airport state for web user on ${wing} page`);
        dispatch(setAirportData({
          airport: 'LOS',
          terminal: '1',
          wing: wing,
          set: true
        }));
      }
    }
  }, [navigate, dispatch, airportState, retryCount]);

  // On Capacitor, don't render children until display mode is resolved
  // This prevents the default route (arrivals) from flashing before redirect
  if (!modeResolved) {
    return null;
  }

  return children;
};

export default ModeRedirector;
